import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  TablePagination,
  InputAdornment,
} from "@mui/material";
import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { AssinaturasAccessDenied } from "./AssinaturasAccessDenied";
import type {
  UiUser,
  UiApiKey} from "../../../api/ui";
import {
  getUsersUi,
  createUserUi,
  updateUserUi,
  deleteUserUi,
  getApiKeysUi
} from "../../../api/ui";
import { useUiAuth } from "../../../context/UiAuthContext";

export function AssinaturasAdminUsers(): React.ReactElement {
  const { t } = useTranslation();
  const { isAdmin } = useUiAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [users, setUsers] = useState<UiUser[]>([]);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [apiKeys, setApiKeys] = useState<UiApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination State initialized from URL query parameters
  const [page, setPage] = useState<number>(() => {
    const p = parseInt(searchParams.get("page") || "0", 10);
    return isNaN(p) || p < 0 ? 0 : p;
  });
  const [rowsPerPage, setRowsPerPage] = useState<number>(() => {
    const s = parseInt(searchParams.get("size") || "10", 10);
    return isNaN(s) || s <= 0 ? 10 : s;
  });
  // Search State: searchInput for typed text, appliedQuery for executed filter
  const [searchInput, setSearchInput] = useState<string>(() => searchParams.get("q") || "");
  const [appliedQuery, setAppliedQuery] = useState<string>(() => searchParams.get("q") || "");

  // User Modal State
  const [openModal, setOpenModal] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [selectedUser, setSelectedUser] = useState<UiUser | null>(null);

  const [formNome, setFormNome] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formSenha, setFormSenha] = useState("");
  const [formApiKey, setFormApiKey] = useState("");
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  // Delete Modal State
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UiUser | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const updateQueryParams = (newParams: { page?: number; size?: number; q?: string }): void => {
    const targetPage = newParams.page !== undefined ? newParams.page : page;
    const targetSize = newParams.size !== undefined ? newParams.size : rowsPerPage;
    const targetQ = newParams.q !== undefined ? newParams.q : appliedQuery;

    const params: Record<string, string> = {};
    if (targetPage > 0) params.page = String(targetPage);
    if (targetSize !== 10) params.size = String(targetSize);
    if (targetQ.trim()) params.q = targetQ.trim();

    setSearchParams(params, { replace: true });
  };

  const loadData = useCallback(async (
    currentPage = page,
    currentRowsPerPage = rowsPerPage,
    currentQuery = appliedQuery
  ): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [usersData, apiKeysData] = await Promise.all([
        getUsersUi(currentPage, currentRowsPerPage, currentQuery),
        getApiKeysUi(),
      ]);
      const typedUsersData = usersData as { totalElements?: number; content?: UiUser[] };
      if (typeof usersData === "object" && usersData && "content" in usersData) {
        setUsers(typedUsersData.content || []);
        setTotalElements(typedUsersData.totalElements ?? (typedUsersData.content?.length || 0));
      } else if (Array.isArray(usersData)) {
        setUsers(usersData);
        setTotalElements(usersData.length);
      } else {
        setUsers([]);
        setTotalElements(0);
      }
      setApiKeys(apiKeysData);
    } catch (err: unknown) {
      const errorObj = err as { response?: { data?: { message?: string } } };
      const msg = errorObj.response?.data?.message;
      setError(typeof msg === "string" ? msg : t("assinaturas.users.loadError"));
    } finally {
      setLoading(false);
    }
  }, [appliedQuery, page, rowsPerPage, t]);

  useEffect(() => {
    void loadData(page, rowsPerPage, appliedQuery);
  }, [loadData, page, rowsPerPage, appliedQuery]);

  const handleChangePage = (_event: unknown, newPage: number): void => {
    setPage(newPage);
    updateQueryParams({ page: newPage });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    updateQueryParams({ page: 0, size: newRowsPerPage });
  };

  const handleExecuteSearch = (): void => {
    setAppliedQuery(searchInput);
    setPage(0);
    updateQueryParams({ page: 0, q: searchInput });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleExecuteSearch();
    }
  };

  const handleClearSearch = (): void => {
    setSearchInput("");
    setAppliedQuery("");
    setPage(0);
    updateQueryParams({ page: 0, q: "" });
  };

  if (!isAdmin) {
    return <AssinaturasAccessDenied />;
  }

  const handleOpenCreateModal = (): void => {
    setModalMode("create");
    setSelectedUser(null);
    setFormNome("");
    setFormEmail("");
    setFormSenha("");
    const defaultApiKey = apiKeys.length > 0 ? apiKeys[0].xApiKey || apiKeys[0].apiKey || "" : "";
    setFormApiKey(defaultApiKey);
    setModalError(null);
    setOpenModal(true);
  };

  const handleOpenEditModal = (user: UiUser): void => {
    setModalMode("edit");
    setSelectedUser(user);
    setFormNome(user.nome);
    setFormEmail(user.email);
    setFormSenha("");
    setFormApiKey(user["api-key"] || user.apiKey || "");
    setModalError(null);
    setOpenModal(true);
  };

  const handleCloseModal = (): void => {
    setOpenModal(false);
  };

  const handleSaveUser = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setModalError(null);

    if (!formNome || !formEmail || !formApiKey) {
      setModalError(t("assinaturas.users.requiredFieldsError"));
      return;
    }

    if (modalMode === "create" && !formSenha) {
      setModalError(t("assinaturas.users.passwordRequiredError"));
      return;
    }

    setModalLoading(true);

    try {
      if (modalMode === "create") {
        await createUserUi({
          nome: formNome,
          email: formEmail,
          senha: formSenha,
          "api-key": formApiKey,
        });
      } else if (selectedUser) {
        await updateUserUi(selectedUser.id, {
          nome: formNome,
          email: formEmail,
          senha: formSenha || undefined,
          "api-key": formApiKey,
        });
      }

      setOpenModal(false);
      await loadData(page, rowsPerPage, appliedQuery);
    } catch (err: unknown) {
      const errorObj = err as { response?: { data?: { message?: string } } };
      const msg = errorObj.response?.data?.message;
      setModalError(typeof msg === "string" ? msg : t("assinaturas.users.saveError"));
    } finally {
      setModalLoading(false);
    }
  };

  const handleOpenDeleteModal = (user: UiUser): void => {
    setUserToDelete(user);
    setOpenDeleteModal(true);
  };

  const handleConfirmDelete = async (): Promise<void> => {
    if (!userToDelete) return;
    setDeleteLoading(true);

    try {
      await deleteUserUi(userToDelete.id);
      setOpenDeleteModal(false);
      setUserToDelete(null);
      await loadData(page, rowsPerPage, appliedQuery);
    } catch (err: unknown) {
      const errorObj = err as { response?: { data?: { message?: string } } };
      const msg = errorObj.response?.data?.message;
      setError(typeof msg === "string" ? msg : t("assinaturas.users.deleteError"));
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box>
      {/* Top Header Row */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
        <Typography color="#1e293b" component="h1" fontWeight="bold" variant="h4">
          {t("assinaturas.users.title")}
        </Typography>

        <Button
          onClick={handleOpenCreateModal}
          startIcon={<AddIcon />}
          sx={{ bgcolor: "#2F16C9", fontWeight: "bold", height: 40, "&:hover": { bgcolor: "#0C1C69" } }}
          variant="contained"
        >
          {t("assinaturas.users.createUser")}
        </Button>
      </Box>

      {/* Search and Action Toolbar Row */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexGrow: 1, maxWidth: 600 }}>
          <TextField
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton onClick={handleClearSearch} size="small">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            fullWidth
            onChange={(e) => { setSearchInput(e.target.value); }}
            onKeyDown={handleKeyDown}
            placeholder={t("assinaturas.users.searchPlaceholder") || "Pesquisar por nome ou e-mail..."}
            size="small"
            sx={{ bgcolor: "#fff", borderRadius: 1 }}
            value={searchInput}
          />
          <Button
            onClick={handleExecuteSearch}
            size="medium"
            startIcon={<SearchIcon />}
            sx={{
              borderColor: "#2F16C9",
              color: "#2F16C9",
              fontWeight: "bold",
              textTransform: "none",
              height: 40,
              whiteSpace: "nowrap",
              "&:hover": { borderColor: "#0C1C69", color: "#0C1C69" },
            }}
            variant="outlined"
          >
            {t("assinaturas.users.searchLabel") || "Pesquisar"}
          </Button>
        </Box>

        <IconButton
          onClick={() => { void loadData(page, rowsPerPage, appliedQuery); }}
          sx={{ bgcolor: "#fff", border: "1px solid #cbd5e1", borderRadius: 1, width: 40, height: 40 }}
          title={t("assinaturas.users.refresh")}
        >
          <RefreshIcon />
        </IconButton>
      </Box>

      {error ? <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert> : null}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ borderRadius: 2, boxShadow: 2, overflow: "hidden" }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: "#f8fafc" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>{t("assinaturas.users.columnName")}</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>{t("assinaturas.users.columnEmail")}</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>{t("assinaturas.users.columnRole")}</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>{t("assinaturas.users.columnApiKey")}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>
                    {t("assinaturas.users.columnActions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell align="center" colSpan={5} sx={{ py: 4, color: "text.secondary" }}>
                      {t("assinaturas.users.noUsers")}
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((row) => (
                    <TableRow hover key={row.id}>
                      <TableCell sx={{ fontWeight: 500 }}>{row.nome}</TableCell>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>
                        <Chip
                          color={row.role === "ADMIN" ? "primary" : "default"}
                          label={row.role || "USER"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <code>{row.apiKeyAnonymized || "****"}</code>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={t("assinaturas.users.editUser")}>
                          <IconButton color="primary" onClick={() => { handleOpenEditModal(row); }}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("assinaturas.users.deleteUser")}>
                          <IconButton color="error" onClick={() => { handleOpenDeleteModal(row); }}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalElements}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            showFirstButton
            showLastButton
          />
        </Paper>
      )}

      {/* Modal Criar / Editar Usuário */}
      <Dialog
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            p: 1.5,
          },
        }}
        fullWidth
        maxWidth="md"
        onClose={handleCloseModal}
        open={openModal}
      >
        <DialogTitle sx={{ fontWeight: "bold", fontSize: "1.35rem" }}>
          {modalMode === "create" ? t("assinaturas.users.modalCreateTitle") : t("assinaturas.users.modalEditTitle")}
        </DialogTitle>
        <Box component="form" onSubmit={(e) => { void handleSaveUser(e); }}>
          <DialogContent>
            {modalError ? <Alert severity="error" sx={{ mb: 2 }}>
                {modalError}
              </Alert> : null}

            <TextField
              fullWidth
              label={t("assinaturas.users.fullName")}
              margin="dense"
              onChange={(e) => { setFormNome(e.target.value); }}
              required
              sx={{ mb: 2 }}
              value={formNome}
            />

            <TextField
              fullWidth
              label={t("assinaturas.users.emailAddress")}
              margin="dense"
              onChange={(e) => { setFormEmail(e.target.value); }}
              required
              sx={{ mb: 2 }}
              type="email"
              value={formEmail}
            />

            <TextField
              fullWidth
              label={modalMode === "create" ? t("assinaturas.users.password") : t("assinaturas.users.newPasswordHint")}
              margin="dense"
              onChange={(e) => { setFormSenha(e.target.value); }}
              required={modalMode === "create"}
              sx={{ mb: 2 }}
              type="password"
              value={formSenha}
            />

            {apiKeys.length > 0 ? (
              <TextField
                fullWidth
                label={t("assinaturas.users.apiKey")}
                margin="dense"
                onChange={(e) => { setFormApiKey(e.target.value); }}
                required
                select
                value={formApiKey}
              >
                {apiKeys.map((item) => {
                  const keyValue = item.xApiKey || item.apiKey || "";
                  const anonymized =
                    item.apiKeyAnonymized ||
                    (keyValue.length > 4 ? `****${keyValue.slice(-4)}` : keyValue || "****");
                  const companyLabel = item.company || item.mainTenant || item.tenant || anonymized;
                  return (
                    <MenuItem key={keyValue} value={keyValue}>
                      {companyLabel} ({anonymized})
                    </MenuItem>
                  );
                })}
              </TextField>
            ) : (
              <TextField
                fullWidth
                label={t("assinaturas.users.apiKey")}
                margin="dense"
                onChange={(e) => { setFormApiKey(e.target.value); }}
                required
                value={formApiKey}
              />
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button color="inherit" onClick={handleCloseModal}>
              {t("assinaturas.users.cancel")}
            </Button>
            <Button
              disabled={modalLoading}
              sx={{ bgcolor: "#2F16C9", "&:hover": { bgcolor: "#0C1C69" } }}
              type="submit"
              variant="contained"
            >
              {modalLoading ? <CircularProgress color="inherit" size={20} /> : t("assinaturas.users.save")}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Modal Confirmar Exclusão */}
      <Dialog onClose={() => { setOpenDeleteModal(false); }} open={openDeleteModal}>
        <DialogTitle sx={{ fontWeight: "bold" }}>{t("assinaturas.users.deleteConfirmTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {t("assinaturas.users.deleteConfirmMessage", { name: userToDelete?.nome, email: userToDelete?.email })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button color="inherit" onClick={() => { setOpenDeleteModal(false); }}>
            {t("assinaturas.users.cancel")}
          </Button>
          <Button
            color="error"
            disabled={deleteLoading}
            onClick={() => { void handleConfirmDelete(); }}
            variant="contained"
          >
            {deleteLoading ? <CircularProgress color="inherit" size={20} /> : t("assinaturas.users.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssinaturasAdminUsers;
